import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Avatar from '../components/Avatar';
import StarRating from '../components/StarRating';
import RideCard from '../components/RideCard';

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const LogoutIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
    <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
  </svg>
);

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--navy-border)' }}>
      <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { currentUser, getUserById, rides, logout } = useApp();

  const user = getUserById(id);
  const isOwnProfile = currentUser?.id === id;

  const userRides = useMemo(() => {
    if (!user) return [];
    return rides
      .filter(r => r.driverId === user.id && r.status === 'open')
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 3);
  }, [rides, user]);

  const memberSince = useMemo(() => {
    if (!user) return '';
    const d = new Date(user.memberSince);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [user]);

  if (!user) {
    return (
      <div className="page" style={{ textAlign: 'center', paddingTop: 80 }}>
        <p style={{ color: 'var(--gray-400)' }}>User not found.</p>
        <button className="btn btn-ghost" onClick={() => navigate(-1)} style={{ marginTop: 16 }}>Go Back</button>
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, paddingTop: 8 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', padding: 4, color: 'var(--gray-300)' }}>
          <ChevronLeft />
        </button>
        <h2 style={{ fontSize: 17, fontWeight: 700 }}>Profile</h2>
        {isOwnProfile ? (
          <button onClick={() => navigate('/edit-profile')} style={{ background: 'none', color: 'var(--accent)', padding: 4 }}>
            <EditIcon />
          </button>
        ) : <div style={{ width: 28 }} />}
      </div>

      {/* Avatar + name */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ position: 'relative', display: 'inline-block', marginBottom: 14 }}>
          <Avatar user={user} size={88} />
          {user.isVerified && (
            <div style={{
              position: 'absolute', bottom: 2, right: 2,
              width: 22, height: 22, borderRadius: '50%',
              background: 'var(--green)', border: '2px solid var(--navy)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12,
            }}>✓</div>
          )}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>{user.name}</h1>
        <p style={{ fontSize: 14, color: 'var(--gray-400)', marginBottom: 10 }}>{user.university}</p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {user.isVerified && (
            <span className="badge badge-verified">✓ Verified Student</span>
          )}
          {user.isDriver && (
            <span className="badge badge-blue">🚗 Driver</span>
          )}
          {user.isRider && (
            <span className="badge" style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8' }}>🎒 Rider</span>
          )}
        </div>

        <StarRating rating={user.rating} count={user.ratingCount} size={16} />
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 1, marginBottom: 20, borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--navy-border)' }}>
        {[
          { val: user.completedRides, lbl: 'Rides' },
          { val: user.rating > 0 ? user.rating.toFixed(1) : '—', lbl: 'Rating' },
          { val: memberSince, lbl: 'Member Since' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: '14px 8px', textAlign: 'center',
            background: 'var(--navy-light)',
            borderRight: i < 2 ? '1px solid var(--navy-border)' : 'none',
          }}>
            <div style={{ fontSize: i === 2 ? 11 : 20, fontWeight: 700, color: 'var(--white)', marginBottom: 2 }}>{s.val}</div>
            <div style={{ fontSize: 10, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.lbl}</div>
          </div>
        ))}
      </div>

      {/* Bio */}
      {user.bio && (
        <div className="card" style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.7 }}>{user.bio}</p>
        </div>
      )}

      {/* Details */}
      <div className="card" style={{ marginBottom: 20 }}>
        <InfoRow label="University" value={user.university} />
        <InfoRow label="Hometown" value={user.hometown} />
        {user.isDriver && user.car && (
          <InfoRow label="Car" value={`${user.car.color} ${user.car.make} ${user.car.model}`} />
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
          <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>Member Since</span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{memberSince}</span>
        </div>
      </div>

      {/* Active rides (driver) */}
      {user.isDriver && userRides.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Available Rides</h3>
          {userRides.map(r => <RideCard key={r.id} ride={r} showDriver={false} compact />)}
        </div>
      )}

      {/* Own profile actions */}
      {isOwnProfile && (
        <div style={{ marginTop: 8 }}>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="btn btn-ghost btn-full"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--red)' }}
          >
            <LogoutIcon /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
